import { type SetStateAction, useCallback, useMemo, useState } from "react";
import {
	hasInvalidFormInput,
	inputDraftSnapshot,
} from "@/lib/form-input-draft";
// Compare semantic JSON data while retaining raw editor text in draft identity.
export function formSnapshot(value: unknown): string {
	return JSON.stringify(inputDraftSnapshot(value)) ?? "";
}

/** The record and schema a draft was opened against, as supplied by the owner. */
interface Source<T, R> {
	sessionKey: string | number;
	initial: T;
	initialKey: string;
	revision: R | undefined;
	revisionKey: string;
}

interface Draft<T, R> {
	source: Source<T, R>;
	/** Identity of the editing session; callbacks from an older one are ignored. */
	token: object;
	baseline: T;
	values: T;
	/** Snapshot of the record this draft saved, awaiting its query refresh. */
	acceptedKey: string | undefined;
}

function fresh<T, R>(source: Source<T, R>, token: object): Draft<T, R> {
	return {
		source,
		token,
		baseline: source.initial,
		values: source.initial,
		acceptedKey: undefined,
	};
}

function isStale<T, R>(draft: Draft<T, R>, source: Source<T, R>): boolean {
	return (
		draft.source.initialKey !== source.initialKey ||
		draft.source.revisionKey !== source.revisionKey
	);
}

/** Decide what a draft becomes when its owner renders with a new source. */
function reconcile<T, R>(
	draft: Draft<T, R>,
	source: Source<T, R>,
): Draft<T, R> {
	if (source.sessionKey !== draft.source.sessionKey) return fresh(source, {});
	if (!isStale(draft, source)) return draft;
	const valuesKey = formSnapshot(draft.values);
	const clean =
		valuesKey === formSnapshot(draft.baseline) ||
		valuesKey === source.initialKey;
	if (clean) return fresh(source, draft.token);
	// Only a matching saved snapshot acknowledges this request. A different
	// source change must remain a conflict, even if no save refetch arrived.
	if (
		draft.acceptedKey === source.initialKey &&
		draft.source.revisionKey === source.revisionKey
	) {
		return {
			...draft,
			source,
			baseline: source.initial,
			acceptedKey: undefined,
		};
	}
	return draft;
}

export function useFormDraft<T, R = unknown>(
	sessionKey: string | number,
	initial: T,
	revision?: R,
) {
	const revisionKey = useMemo(() => formSnapshot(revision), [revision]);
	const source: Source<T, R> = {
		sessionKey,
		initial,
		initialKey: formSnapshot(initial),
		revision,
		revisionKey,
	};
	const [state, setState] = useState(() => fresh(source, {}));
	// Reset this component before its children render a different record.
	const current = reconcile(state, source);
	if (current !== state) setState(current);
	const token = current.token;
	const updateCurrent = useCallback(
		(update: (previous: Draft<T, R>) => Draft<T, R>) => {
			setState((previous) =>
				previous.token === token ? update(previous) : previous,
			);
		},
		[token],
	);
	const setValues = useCallback(
		(next: SetStateAction<T>) =>
			updateCurrent((previous) => ({
				...previous,
				values:
					typeof next === "function"
						? (next as (value: T) => T)(previous.values)
						: next,
			})),
		[updateCurrent],
	);
	const reset = () =>
		updateCurrent((previous) => fresh(source, previous.token));
	// The submitted raw draft and normalized saved record are explicit so save
	// completion never depends on which render supplied this callback.
	const accept = useCallback(
		(submitted: T, saved: T) =>
			updateCurrent((previous) => ({
				...previous,
				baseline: submitted,
				acceptedKey: formSnapshot(saved),
			})),
		[updateCurrent],
	);
	// A save the server answered with its canonical record: `settle` rebuilds the
	// draft against what was stored (a partial save keeps unrelated edits), and
	// `saved` is the record's snapshot the refetch is expected to deliver.
	const commit = useCallback(
		(
			settle: (previous: { values: T; baseline: T }) => {
				values: T;
				baseline: T;
			},
			saved: T,
		) =>
			updateCurrent((previous) => ({
				...previous,
				...settle(previous),
				acceptedKey: formSnapshot(saved),
			})),
		[updateCurrent],
	);
	const valuesKey = useMemo(
		() => formSnapshot(current.values),
		[current.values],
	);
	const baselineKey = useMemo(
		() => formSnapshot(current.baseline),
		[current.baseline],
	);
	const dirty = valuesKey !== baselineKey;
	const hasConflict = dirty && isStale(current, source);
	const hasInvalidInput = useMemo(
		() => hasInvalidFormInput(current.values),
		[current.values],
	);
	return {
		values: current.values,
		revision: current.source.revision,
		baseline: current.baseline,
		setValues,
		reset,
		accept,
		commit,
		field<K extends keyof T>(
			key: K,
		): [T[K], (next: SetStateAction<T[K]>) => void] {
			return [
				current.values[key],
				(next) =>
					setValues((previous) => ({
						...previous,
						[key]:
							typeof next === "function"
								? (next as (value: T[K]) => T[K])(previous[key])
								: next,
					})),
			];
		},
		canSubmit: !hasConflict && !hasInvalidInput,
		hasInvalidInput,
		dirty,
		hasConflict,
	};
}

/**
 * Ancillary editing state — errors, confirmations, touched flags — for the same
 * session a draft belongs to. It describes the record being edited, not the
 * values, so a new record starts clean while a refetch of the same one does not
 * clear a warning the author is still reading.
 */
export function useSessionState<T>(sessionKey: string | number, initial: T) {
	const [state, setState] = useState(() => ({
		sessionKey,
		token: {},
		value: initial,
	}));
	let current = state;
	if (sessionKey !== state.sessionKey) {
		current = { sessionKey, token: {}, value: initial };
		setState(current);
	}
	const token = current.token;
	const setValue = useCallback(
		(next: SetStateAction<T>) => {
			setState((previous) =>
				previous.token !== token
					? previous
					: {
							...previous,
							value:
								typeof next === "function"
									? (next as (value: T) => T)(previous.value)
									: next,
						},
			);
		},
		[token],
	);
	return [current.value, setValue] as const;
}
